import { z } from "zod";

export const materialCategorySchema = z.enum(["raw", "manufactured", "encoded"]);
export type MaterialCategory = z.infer<typeof materialCategorySchema>;

export const materialSchema = z.object({
  key: z.string(),
  name: z.string(),
  category: materialCategorySchema,
  rarity: z.string(),
  rarityRank: z.number().int().min(1).max(5),
  group: z.string().nullable(),
  origins: z.array(z.string()),
});
export type Material = z.infer<typeof materialSchema>;

export const ingredientSchema = z.object({
  materialKey: z.string().nullable(),
  name: z.string(),
  size: z.number(),
});
export type Ingredient = z.infer<typeof ingredientSchema>;

export const blueprintGradeEffectSchema = z.object({
  property: z.string(),
  effect: z.string(),
  isGood: z.boolean(),
});
export type BlueprintGradeEffect = z.infer<typeof blueprintGradeEffectSchema>;

export const ingredientSourceSchema = z.enum([
  "uuid",
  "heuristic",
  "unverified",
  "coriolis-only",
]);
export type IngredientSource = z.infer<typeof ingredientSourceSchema>;

export const blueprintGradeSchema = z.object({
  grade: z.number().int().min(1).max(5),
  ingredients: z.array(ingredientSchema).nullable(),
  engineers: z.array(z.string()),
  effects: z.array(blueprintGradeEffectSchema),
  ingredientsSource: ingredientSourceSchema,
  modifiers: z.record(z.string(), z.unknown()),
});
export type BlueprintGrade = z.infer<typeof blueprintGradeSchema>;

export const dataStatusSchema = z.enum(["verified", "partial", "unverified"]);
export type DataStatus = z.infer<typeof dataStatusSchema>;

export const blueprintSchema = z.object({
  key: z.string(),
  fdName: z.string().nullable(),
  moduleKeys: z.array(z.string()),
  moduleName: z.string().nullable(),
  name: z.string(),
  displayName: z.string(),
  grades: z.array(blueprintGradeSchema),
  dataStatus: dataStatusSchema,
});
export type Blueprint = z.infer<typeof blueprintSchema>;

export const experimentalEffectSchema = z.object({
  key: z.string(),
  id: z.number().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  ingredients: z.array(ingredientSchema),
});
export type ExperimentalEffect = z.infer<typeof experimentalEffectSchema>;

export const shipSlotKindSchema = z.enum(["core", "hardpoint", "utility", "optional", "bulkhead"]);
export type ShipSlotKind = z.infer<typeof shipSlotKindSchema>;

export const shipBuildSlotSchema = z.object({
  slotName: z.string(),
  kind: shipSlotKindSchema,
  size: z.number().nullable(),
  index: z.number().int(),
  group: z.string().nullable(),
  label: z.string().nullable(),
  eligible: z.array(z.string()).nullable(),
});
export type ShipBuildSlot = z.infer<typeof shipBuildSlotSchema>;

export const shipBulkheadSchema = z.object({
  name: z.string(),
  symbol: z.string().nullable(),
  cost: z.number().nullable(),
  mass: z.number().nullable(),
  hullBoost: z.number().nullable(),
  kineticResistance: z.number().nullable(),
  thermalResistance: z.number().nullable(),
  explosiveResistance: z.number().nullable(),
  causticResistance: z.number().nullable(),
});
export type ShipBulkhead = z.infer<typeof shipBulkheadSchema>;

export const shipDefaultModuleSchema = z.object({
  slotName: z.string(),
  kind: shipSlotKindSchema,
  moduleSymbol: z.string(),
  moduleName: z.string(),
});
export type ShipDefaultModule = z.infer<typeof shipDefaultModuleSchema>;

export const shipSchema = z.object({
  slug: z.string(),
  fdSymbol: z.string().nullable(),
  name: z.string(),
  manufacturer: z.string().nullable(),
  class: z.number().nullable(),
  hullCost: z.number().nullable(),
  retailCost: z.number().nullable(),
  speed: z.number().nullable(),
  boost: z.number().nullable(),
  boostEnergy: z.number().nullable(),
  hullMass: z.number().nullable(),
  masslock: z.number().nullable(),
  baseShieldStrength: z.number().nullable(),
  baseArmour: z.number().nullable(),
  hardness: z.number().nullable(),
  heatCapacity: z.number().nullable(),
  crew: z.number(),
  pitch: z.number().nullable(),
  roll: z.number().nullable(),
  yaw: z.number().nullable(),
  reserveFuelCapacity: z.number().nullable(),
  slots: z.object({
    core: z.array(z.number().nullable()),
    /**
     * `null` entries are slots whose size is not published in the coriolis
     * dataset (e.g. Type-11 Prospector hardpoints); the app shows them as
     * "size unknown" instead of guessing.
     */
    hardpoints: z.array(
      z.union([
        z.number().nullable(),
        z.object({ class: z.number().nullable(), name: z.string().nullable() }),
      ]),
    ),
    optional: z.array(
      z.union([z.number().nullable(), z.object({ class: z.number(), name: z.string().nullable() })]),
    ),
  }),
  buildSlots: z.array(shipBuildSlotSchema),
  defaultModules: z.array(shipDefaultModuleSchema).default([]),
  bulkheads: z.array(shipBulkheadSchema),
});
export type Ship = z.infer<typeof shipSchema>;

export const moduleStatValueSchema = z.union([z.number(), z.boolean()]);
export type ModuleStatValue = z.infer<typeof moduleStatValueSchema>;

export const preEngineeredModuleSchema = z.object({
  sourceId: z.string(),
  grade: z.number().int().nullable(),
  blueprints: z.array(z.string()),
  reengineerable: z.boolean().nullable(),
  gradeChangeable: z.boolean().nullable(),
  canApplyExperimental: z.boolean().nullable(),
  description: z.string().nullable(),
});
export type PreEngineeredModule = z.infer<typeof preEngineeredModuleSchema>;

export const moduleDefSchema = z.object({
  symbol: z.string(),
  category: z.string(),
  name: z.string(),
  ship: z.string().nullable(),
  class: z.number().nullable(),
  rating: z.string().nullable(),
  mount: z.string().nullable(),
  guidance: z.string().nullable(),
  group: z.string().nullable(),
  cost: z.number().nullable(),
  mass: z.number().nullable(),
  power: z.number().nullable(),
  integrity: z.number().nullable(),
  stats: z.record(z.string(), moduleStatValueSchema),
  preEngineered: preEngineeredModuleSchema.nullable().default(null),
});
export type ModuleDef = z.infer<typeof moduleDefSchema>;

export const engineerDefSchema = z.object({
  key: z.string(),
  id: z.number(),
  name: z.string(),
  systemAddress: z.number().nullable(),
  marketId: z.number().nullable(),
});

export const engineerKindSchema = z.enum(["ship", "on-foot"]);
export type EngineerKind = z.infer<typeof engineerKindSchema>;

/**
 * Community-curated engineer locations and access requirements
 * (engineer-details.json). Ship engineers have an unlock task and reputation
 * hints; on-foot (Odyssey) engineers have suit/weapon modification lists and
 * optionally a referral task that reveals the next engineer in their chain.
 */
export const engineerDetailsEntrySchema = z.discriminatedUnion("kind", [
  z.object({
    key: z.string().min(1),
    kind: z.literal("ship"),
    system: z.string().min(1),
    base: z.string().min(1),
    planet: z.string().nullable(),
    region: z.string().min(1),
    permitRequired: z.boolean().optional(),
    discovery: z.string().min(1),
    invite: z.string().min(1),
    unlock: z.string().min(1),
    reputation: z.string().min(1),
  }),
  z.object({
    key: z.string().min(1),
    kind: z.literal("on-foot"),
    system: z.string().min(1),
    base: z.string().min(1),
    planet: z.string().nullable(),
    region: z.string().min(1),
    permitRequired: z.boolean().optional(),
    discovery: z.string().min(1),
    invite: z.string().min(1),
    referral: z.string().nullable(),
    refersTo: z.string().nullable(),
    offers: z.array(z.string().min(1)).min(1),
  }),
]);
export type EngineerDetailsEntry = z.infer<typeof engineerDetailsEntrySchema>;

export const engineerDetailsFileSchema = z.object({
  generatedAt: z.string(),
  source: z.string(),
  sourceUrl: z.string(),
  retrievedAt: z.string(),
  note: z.string(),
  engineers: z.array(engineerDetailsEntrySchema),
});

/** Normalized engineer details merged into {@link EngineerDef}. */
export interface EngineerDetails {
  kind: EngineerKind;
  system: string;
  base: string;
  planet: string | null;
  region: string;
  permitRequired: boolean;
  discovery: string;
  invite: string;
  /** Ship engineers: the task that unlocks their services. */
  unlock: string | null;
  /** Ship engineers: how to gain reputation for higher grades. */
  reputation: string | null;
  /** On-foot engineers: task that earns a referral to the next engineer. */
  referral: string | null;
  /** On-foot engineers: engineer key this referral leads to. */
  refersToKey: string | null;
  /** On-foot engineers: suit and weapon modifications they offer. */
  offers: string[];
}

export type EngineerDef = z.infer<typeof engineerDefSchema> & EngineerDetails;

export const commodityDefSchema = z.object({
  symbol: z.string(),
  name: z.string(),
});
export type CommodityDef = z.infer<typeof commodityDefSchema>;

export const rankDefSchema = z.object({
  rank: z.number().int(),
  name: z.string(),
});
export type RankDef = z.infer<typeof rankDefSchema>;

export const ranksSchema = z.object({
  combat: z.array(rankDefSchema),
  trade: z.array(rankDefSchema),
  exploration: z.array(rankDefSchema),
  exobiology: z.array(rankDefSchema),
  federation: z.array(rankDefSchema),
  empire: z.array(rankDefSchema),
  cqc: z.array(rankDefSchema),
});
export type Ranks = z.infer<typeof ranksSchema>;

export const gameDataMetaSchema = z.object({
  generatedAt: z.string(),
  generator: z.string(),
  gameDataNote: z.string(),
  sources: z.record(z.string(), z.string()),
  counts: z.record(z.string(), z.unknown()),
  integrity: z.record(z.string(), z.unknown()),
  files: z.array(
    z.object({
      file: z.string(),
      bytes: z.number(),
      sha256: z.string(),
    }),
  ),
});
export type GameDataMeta = z.infer<typeof gameDataMetaSchema>;
